import { useEffect, useMemo, useRef, useState } from 'react';
import type { BrandAssets, Tenant } from '@mg/shared';
import { api } from '../../../lib/api';
import { useTenant } from '../../../auth/TenantContext';
import { useFooterSave } from '../../../components/settings/footerSave';
import { ColorsCard, TypographyCard, VisualAssetsCard } from '../../../components/workspace/BrandAssetsEditor';
import { initBrandAssets } from '../../../components/workspace/brandAssetsDefaults';
import { EditSection } from '../EditSection';
import { PageHeadingCard } from '../PageHeadingCard';
import { saveErrorText } from '../brand/useBrandInformationForm';
import { assetsPayload, assetsStyleChanged } from './assetsPayload';

export const BRAND_ASSETS_PAGE_HEADING = {
  title: 'Brand assets and style',
  description: "Colors, fonts and logos for your brand's designs and posts.",
} as const;

/** Each card's one-line description under its plain title (O18). */
const CARD_DESCRIPTIONS = {
  colors: "Your brand's primary and secondary colors and where each is used.",
  typography: 'The fonts your brand uses for headings, body text and accents.',
  logos: "Your brand's logo in its standard, dark and icon versions.",
} as const;

/**
 * The Brand assets and style page (spec:166, :184-185; web.md §6.3): the heading card, then Color
 * Schemes, Fonts/Typography and Logos & Visual Assets, with today's fields and copy, saved by the
 * page's sticky footer. Social Hashtags and Catchphrases & Slogans are not here any more: they are
 * Vocabulary on the Brand information page. The save sends colors, typography and logos only (E4).
 *
 * A stored `brandAssets` may be partial (only hashtags and phrases, written by the Brand information
 * page): initBrandAssets shows the starter values for what is missing, as it does for none at all.
 * Logo files upload as soon as they are chosen; only their address waits for Save, as before.
 */
export function BrandAssetsPage({ workspace, canEdit }: { workspace: Tenant; canEdit: boolean }) {
  const { refresh } = useTenant();
  const stored = workspace.brandAssets;
  const saved = useMemo(() => initBrandAssets(stored), [stored]);
  const [assets, setAssets] = useState<BrandAssets>(() => initBrandAssets(stored));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLParagraphElement>(null);
  const dirty = assetsStyleChanged(assets, saved);

  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [error]);

  const save = async (): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      await api.saveBrandAssetsStyle(workspace.id, assetsPayload(assets));
      // The workspace list carries brandAssets: the saved values come back through it.
      await refresh();
      return true;
    } catch (err) {
      setError(saveErrorText(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  useFooterSave({
    id: 'brand-assets',
    dirty,
    canSave: canEdit,
    saving,
    save,
    discard: () => {
      setAssets(initBrandAssets(stored));
      setError(null);
    },
  });

  const disabled = !canEdit || saving;
  return (
    <>
      <PageHeadingCard title={BRAND_ASSETS_PAGE_HEADING.title} description={BRAND_ASSETS_PAGE_HEADING.description} />
      <EditSection page="assets" id="color-schemes">
        <ColorsCard
          value={assets}
          onChange={setAssets}
          disabled={disabled}
          plainHeader={{ description: CARD_DESCRIPTIONS.colors }}
        />
      </EditSection>
      <EditSection page="assets" id="fonts-typography">
        <TypographyCard
          value={assets}
          onChange={setAssets}
          disabled={disabled}
          plainHeader={{ description: CARD_DESCRIPTIONS.typography }}
        />
      </EditSection>
      <EditSection page="assets" id="logos-visual-assets">
        <VisualAssetsCard
          value={assets}
          onChange={setAssets}
          disabled={disabled}
          plainHeader={{ description: CARD_DESCRIPTIONS.logos }}
        />
      </EditSection>
      {error && (
        <p ref={errorRef} role="alert" className="text-sm text-red-600 dark:text-error-dark">
          {error}
        </p>
      )}
    </>
  );
}
